import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import Toggle from 'shared/components/toggle/toggle'
import css from './header.css'
import SuggestedTerms from '../suggested-terms/suggested-terms'
import SearchTypeToggle from 'shared/containers/search-type-toggle'
import { AvatarBadgeCarousel } from 'shared/components/users'
import { toggleAutoPlay } from 'shared/redux/auto-play'
import { getAutoPlay } from 'shared/redux/reducers'

export const Results = ({ css, term, displayTerm, gifCount, isSticker, user: { username } }) => {
    const getTitle = (displayTerm, username) => {
        if (displayTerm) {
            return displayTerm
        } else if (username) {
            return `All the ${username} GIFs`
        } else {
            return term
        }
    }
    return (
        <div className={css.results}>
            <h1 className={css.searchTerm}>
                {getTitle(displayTerm, username)}
                {typeof gifCount === 'number' && (
                    <span data-gif-count={gifCount.toLocaleString()} className={css.number}>
                        {` ${isSticker ? 'Sticker' : 'GIF'}${gifCount === 1 ? '' : 's'}`}
                    </span>
                )}
            </h1>
        </div>
    )
}

const Sort = ({ sorting }) => (
    <div className={css.sort}>
        <span>
            Sort:
            <a href="?sort=relevant" className={sorting !== 'recent' ? css.activeSortLink : css.sortLink}>
                Relevant
            </a>
            <a href="?sort=recent" className={sorting === 'recent' ? css.activeSortLink : css.sortLink}>
                Newest
            </a>
        </span>
    </div>
)

const AutoPlayLabel = () => <span className={css.autoplayLabel}>Auto Play</span>

const AutoPlay = ({ autoPlay, onToggleAutoPlay }) => (
    <Toggle
        isOn={autoPlay}
        onElement={<i className="ss-play" />}
        offElement={<i className="ss-pause" />}
        toggle={onToggleAutoPlay}
    />
)

const Header = ({ autoPlay, sorting, onToggleAutoPlay, canSort = true }) => (
    <div className={css.header}>
        <div>
            {canSort && <Sort sorting={sorting} />}
            <div className={css.autoplay}>
                <AutoPlay {...{ autoPlay, onToggleAutoPlay }} />
            </div>
        </div>
    </div>
)

const NoResults = ({ suggestion, suggestionUrl, isSticker, displayTerm }) => {
    const hasSuggestion = !isEmpty(suggestion)
    const prompt = hasSuggestion ? `Did you mean` : `Try searching for`
    const url = hasSuggestion ? suggestionUrl : `/search/${displayTerm}${isSticker ? `` : `-stickers`}`
    const label = hasSuggestion ? suggestion : isSticker ? `GIFs ` : `Stickers `
    const punctuation = hasSuggestion ? '?' : 'instead?'
    return (
        <div className={css.noResults}>
            <span>{`No ${isSticker ? `Stickers` : `GIFs`} found for ${displayTerm}`}</span>
            <span>
                {prompt} <a href={url}>{label}</a>
                {punctuation}
            </span>
        </div>
    )
}

const UserNotVerifiedError = ({ user: { username } }) => {
    return (
        <div className={css.noResults}>
            <span>{`Oops sorry! @${username} does not have a Verified Channel.`}</span>
            <span>
                {prompt} <a href="/faq">Learn more about verification on GIPHY.</a>
            </span>
        </div>
    )
}

const InvalidUserError = ({ term }) => {
    return (
        <div className={css.noResults}>
            <span>{`Oops sorry! ${term} is not a GIPHY Channel.`}</span>
            <span>
                {prompt} <a href="/faq">Learn more about Social Search on GIPHY.</a>
            </span>
        </div>
    )
}

export const AutoPlayHeader = ({ autoPlay, onToggleAutoPlay }) => (
    <div className={css.autoPlayHeader}>
        <AutoPlayLabel />
        <AutoPlay {...{ autoPlay, onToggleAutoPlay }} />
    </div>
)

export const ExploreHeader = connect(
    state => ({ autoPlay: getAutoPlay(state) }),
    {
        onToggleAutoPlay: toggleAutoPlay,
    },
)(
    ({
        autoPlay,
        onToggleAutoPlay,
        searchInfo: { displayTerm, term, sorting },
        css: customCss = {},
        canSort = true,
        isSticker,
    }) => {
        const { exploreUpperHeader = css.exploreUpperHeader, exploreSearchTerm = css.exploreSearchTerm } = customCss
        return (
            <div className={exploreUpperHeader}>
                <div className={css.exploreResults}>
                    <h1 className={exploreSearchTerm}>
                        Explore <span className={css.highlight}>{displayTerm || term}</span>{' '}
                        {isSticker ? 'Stickers' : 'GIFs'}
                    </h1>
                </div>
                <div className={css.exploreHeader}>
                    <div>
                        {canSort && <Sort sorting={sorting} />}
                        <div className={css.autoplay}>
                            <AutoPlay {...{ autoPlay, onToggleAutoPlay }} />
                        </div>
                    </div>
                </div>
            </div>
        )
    },
)

export const ErrorHandlingHeader = ({ isSticker, searchInfo }) => {
    const { term, displayTerm, error } = searchInfo
    switch (error.type) {
        case 'no_results_suggestion':
            return <NoResults {...searchInfo} isSticker={isSticker} term={term} displayTerm={displayTerm} />
        case 'user_not_verified':
            return <UserNotVerifiedError {...searchInfo} />
        case 'invalid_user':
            return <InvalidUserError {...searchInfo} />
        default:
            return null
    }
}

const SearchHeader = ({ searchResponseId, isSticker, autoPlay, searchInfo, toggleAutoPlay, channels = [], user }) => {
    const { sorting, term, error, user: searchUser } = searchInfo

    return (
        <div>
            <div className={css.upperHeader}>
                <Results css={css} {...searchInfo} gifCount={searchInfo.total} isSticker={isSticker} />
                <SuggestedTerms
                    isMobile={false}
                    searchResponseId={searchResponseId}
                    css={css}
                    user={user}
                    {...searchInfo}
                    isSticker={isSticker}
                />
            </div>
            {!searchUser.username && channels.length > 0 && (
                <AvatarBadgeCarousel
                    searchResponseId={searchResponseId}
                    items={channels}
                    user={user}
                    css={css}
                    paginates={true}
                />
            )}
            <SearchTypeToggle className={css.searchTypeToggle} term={`${term}${isSticker ? ` sticker` : ``}`} />
            {error ? (
                <ErrorHandlingHeader isSticker={isSticker} searchInfo={searchInfo} />
            ) : (
                <Header
                    autoPlay={autoPlay}
                    sorting={sorting}
                    onToggleAutoPlay={toggleAutoPlay}
                    term={term}
                    isSticker={isSticker}
                />
            )}
        </div>
    )
}

SearchHeader.propTypes = {
    autoPlay: PropTypes.bool.isRequired,
    searchInfo: PropTypes.shape({
        term: PropTypes.string.isRequired,
        total: PropTypes.number.isRequired,
        sorting: PropTypes.string,
        searchResponseId: PropTypes.string,
        suggestion: PropTypes.string,
        suggestionUrl: PropTypes.string,
        isSticker: PropTypes.bool.isRequired,
    }),
    toggleAutoPlay: PropTypes.func.isRequired,
    channels: PropTypes.array,
}

export default SearchHeader
